import { GridRowsProp } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../../app-context';
import IGridComponent from '../../components/grid/i-grid.component';
import TitlePanel from '../../components/title-panel/title-panel.component';
import { ALL, ORDER_STATUS_SELECT_ITEMS, GET_ALL_CUSTOMER_URL, GET_ALL_PRODUCT_URL, 
         PAGE_SIZE, ORDER_TYPE_SELECT_ITEMS, DAYS_YEAR} from '../common/constants';
import { CustomGridColDef, formatDate, formatTo0Decimal, formatTo2Decimal, isEmptyArray } from '../common/util.services';
import './order-list.component.scss';
import { searchOrder } from './order.services';
import SearchPanel from '../../components/search-panel/search-panel.component';
import { Grid, Stack, Tooltip } from '@mui/material';
import ITextField from '../../components/text-component/textfield/i-textfield.component';
import IDropdown from '../../components/text-component/dropdown/i-dropdown.component';
import dayjs from 'dayjs';
import IDateField from '../../components/text-component/i-datefield/i-datefield.component';
import { AssessmentOutlined, ListAltOutlined } from '@mui/icons-material';
import IDialogComponent from '../../components/dialog/i-dialog.component';
import OrderTrend from './order-trend.component';
import ICheckbox from '../../components/checkbox/i-checkbox.component';

const OrderList = () => {
  const appContext = useContext(AppContext);
  const navigate = useNavigate();
  const rows: GridRowsProp = [];
  const [amazon, setAmazon] = useState(true);
  const [orderData, setOrderData] = useState([{rowId: '', customer: 'Loading...', poDetails: ''}]);
  const [openBarChart, setOpenBarChart] = useState(false);
  const popperProps = {sx: {'.MuiTooltip-tooltip': {bgcolor: 'ivory', fontSize: 16, color: 'black', border: 1}}};
  const [searchData, setSearchData] = useState<any>({customer: ALL, product: ALL, status: ALL, type: ALL, priority: ALL,
                                                     startDate: dayjs().subtract(DAYS_YEAR, 'day'), endDate: dayjs()});
  const showOrderTrend = () => {
    setOpenBarChart(true);
  }
  const columns: CustomGridColDef[] = [
    { field: 'code', headerName: 'Ord Code', filterable: false, sortable: false, editable: false, width: 120,
      renderCell: (params) => { return <div className="cell-link" 
      onClick={() => { openEditOrderPage(params.row) }}>{params.row.code}</div> },
      headerIcon: <AssessmentOutlined fontSize='small'/>, includeHeaderName: true, 
      headerIconText:'Order Trend', headerClickAction: showOrderTrend
    },
    { field: 'customer', headerName: 'Customer', filterable: false, sortable: false, editable: false, width: 150 },
    { field: 'priority', headerName: 'Prio', filterable: false, sortable: false, editable: false, width: 75 },
    { field: 'orderValue', headerName: 'Price', filterable: false, sortable: false, editable: false, width: 95,
      valueFormatter: (orderValue: any) => formatTo0Decimal(orderValue)
    },
    { field: 'poValue', headerName: 'PO Value', filterable: false, sortable: false, editable: false, width: 95,
      renderCell: (params) => { return params.row.poDetails.includes('drive.google.com') ?
                                (<Link to={params.row.poDetails} target="_blank">{formatTo0Decimal(params.row.poValue)}</Link>) 
                                : formatTo0Decimal(params.row.poValue) }
    },
    { field: 'margin', headerName: 'Gross/PO', filterable: false, sortable: false, editable: false, width: 80,
      valueFormatter: (margin: any) => formatTo2Decimal(margin) + '%',
      cellClassName: (params) => { return params.row.margin > 0 ? 'Positive' : 'Negative' }
    },
    { field: 'netMargin', headerName: 'Net/PO', filterable: false, sortable: false, editable: false, width: 75,
      valueFormatter: (netMargin: any) => formatTo2Decimal(netMargin) + '%',
      cellClassName: (params) => { return params.row.netMargin > 0 ? 'Positive' : 'Negative' }
    },
    { field: 'productCount', headerName: 'Prod', filterable: false, sortable: false, editable: false, width: 50,
      renderCell: (params) => { return <div className="cell-link" 
      onClick={() => { openProductOrderPage(params.row) }}>{params.row.productCount}</div> }
    },
    { field: 'enquiryCode', headerName: 'Enq Code', filterable: false, sortable: false, editable: false, width: 120,
      renderCell: (params) => { return <div className="cell-link" 
      onClick={() => { openEnquiryOrderPage(params.row) }}>{params.row.enquiryCode}</div> }
    },
    { field: 'status', headerName: 'Status', filterable: false, sortable: false, editable: false, width: 60 },
    { field: 'processing', headerName: 'BOM', filterable: false, sortable: false, editable: false, width: 50,
      renderCell: (params) => { return <div onClick={() => { openProcessingPage(params.row) }}>
                              {<Stack direction="column" alignItems="center" gap={1}>
                               <ListAltOutlined fontSize='medium' color="primary"/></Stack>} </div> }
    },
    { field: 'qltyParams', headerName: 'Qlty', filterable: false, sortable: false, editable: false, width: 75 },
    { field: 'orderDate', headerName: 'Ord Date', filterable: false, sortable: false, editable: false, width: 90,
      renderCell: (params: any) =>  (<Tooltip title={getTooltip(params.row)}
      PopperProps={popperProps}><span>{formatDate(params.row.orderDate)}</span></Tooltip>)
    },
    { field: 'scheduleCount', headerName: 'Sch', filterable: false, sortable: false, editable: false, width: 50,
      renderCell: (params) => { return <div className="cell-link" 
      onClick={() => { openSchedulePage(params.row) }}>{params.row.scheduleCount}</div> }
    },
    { field: 'projectManager', headerName: 'Supervisor', filterable: false, sortable: false, editable: false, width: 125 },
    { field: 'remarks', headerName: 'Remarks', filterable: false, sortable: false, editable: false, width: 150 },
  ];
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    if (isEmptyArray(rows)) {
      setLoading(true);
      searchData.amazon = amazon;
      searchOrder(searchData, page, pageSize, appContext,
        (response: any) => {
          setOrderData(response.data.data);
          setTotalRows(response.data.totalElements);
          appContext.setShowLoader(false);
          setLoading(false);
        }, (error: any) => {
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message);
          setLoading(false);
        });
    }
  }, [searchData, page, pageSize]);

  function getTooltip (row: any) {
    let text = row.daysRemaining + ',' + row.orderAge
    let array = text.split(',');
    array[0] = 'Days Left: ' + array[0];
    array[1] = 'Ageing: ' + array[1];

    let newText = array.map(i => {
      return <p>{i}</p>
    });

    return newText;
  }
  
  const closeBarChart = (e: any) => {
    setOpenBarChart(false);
  }

  const openEditOrderPage = (row: any) => {
    navigate('/enquiry/order/' + row.rowId, {state: {enquiryCode: row.enquiryCode,
                                                     customer: row.customer,
                                                     product: row.product,
                                                     description: row.description}});
  };

  const openSchedulePage = (row: any) => {
    navigate('/production/schedule/' + row.code + '/list', {state: {orderId: row.rowId, orderCode: row.code}});
  };

  const openEnquiryOrderPage = (row: any) => {
    navigate('../enquiry/order/enq/' + row.enquiryCode)
  }

  const openProcessingPage = (row: any) => {
    navigate('/order/processing/' + row.rowId)
  }

  const openProductOrderPage = (row: any) => {
    navigate('/order/product/' + row.rowId)
  }

  const search = (data: any) => {
    setSearchData(data);
  }

  const onPaginationModelChange = (evt: any) => {
    setPage(evt.page);
    setPageSize(evt.pageSize);
  }

  const getSearchComponent = () => {
    return <SearchPanel search={search} formData={searchData}>
      <Grid container spacing={1} alignItems="center">
        <Grid xs={1.5}>
          <ITextField name='searchText' label='Search Text' />
        </Grid>
        <Grid xs={1.5}>
          <IDropdown name='customer' label='Customer' url={GET_ALL_CUSTOMER_URL} allItemRequired={true}/>
        </Grid>
        <Grid xs={1}>
          <ICheckbox name='amazon' label='Amazon' defaultChecked={true}
            onChangeEvent={(e: any) => {setAmazon(e.target.checked)}}/>
        </Grid>
        <Grid xs={2}>
          <IDropdown name='product' label='Product' url={GET_ALL_PRODUCT_URL} allItemRequired={true} />
        </Grid>
        <Grid xs={1}>
          <IDropdown name='status' label='Status' itemList={ORDER_STATUS_SELECT_ITEMS} allItemRequired={true} />
        </Grid>
        <Grid xs={1}>
          <IDropdown name='type' label='Type' itemList={ORDER_TYPE_SELECT_ITEMS} allItemRequired={true} />
        </Grid>
        <Grid xs={1}>
          <IDropdown name='priority' label='Priority' itemList={['High', 'Medium', 'Low']} allItemRequired={true} />
        </Grid>
        <Grid xs={1.5}>
          <IDateField name='startDate' label='Start Date' />
        </Grid>
        <Grid xs={1.5}>
          <IDateField name='endDate' label='End Date' />
        </Grid>
      </Grid></SearchPanel>
  }

  const getMainPanel = () => {
    return <>
      {getSearchComponent()}
      <IGridComponent title={''} rows={orderData} columns={columns} loading={loading} maxHeight={450}
        paginationType="server" paginationModelChange={onPaginationModelChange} totalRows={totalRows}
        showAddBtn={false} showApproveAction={false} showDeleteAction={false} showUpAction={false} showDownAction={false}
        getRowId={(row: any) => row.rowId} />
      <IDialogComponent onClose={closeBarChart} open={openBarChart} title='Order Trend' 
        dialogBody={<OrderTrend />} />
    </>
  }

  return <TitlePanel title='All Customer Orders' panelBody={getMainPanel()} />
}

export default OrderList;